import { DEFAULT_CURRENCY } from '@/constants/orders'
import type { SegmentModel } from '@/types'

import type {
  ImportPriceSettingsDto,
  PriceColumnDto,
  PurchaseDto,
  SegmentPriceMode,
  SegmentRuleDto,
} from '../../types/stockImportsPrices.types'

export const DEFAULT_VAT_RATE_PERCENT = 20

export type PriceSegmentSource = Pick<
  SegmentModel,
  'id' | 'description' | 'markupPercentage' | 'isRrc'
>

export interface PriceSegmentRow {
  segmentId: string
  description: string
  defaultMarkupPercent: number
  isRrc: boolean
  enabled: boolean
  mode: SegmentPriceMode
  useSegmentDefault: boolean
  deltaPercent: string
  columnNumber: string
  columnExpectedHeader: string
}

export interface CurrencyMappingRow {
  key: string
  value: string
}

export interface ConfigPriceFormValues {
  enabled: boolean
  vatRatePercent: string
  purchaseColumnNumber: string
  pricesIncludeVat: boolean
  purchaseDiscountPercent: string
  purchaseExpectedHeader: string
  currencyFromFile: boolean
  currencyFixedCode: string
  currencyColumnNumber: string
  currencyTextMapping: CurrencyMappingRow[]
  rrcColumnNumber: string
  rrcExpectedHeader: string
  segments: PriceSegmentRow[]
  minMarkupPercent: string
  capByRrc: boolean
  spikeAlertPercent: string
  maxInvalidPricePercent: string
}

export const SEGMENT_MODES_FOR_RRC: SegmentPriceMode[] = [
  'RRC_COLUMN',
  'MARKUP',
]
export const SEGMENT_MODES_FOR_REGULAR: SegmentPriceMode[] = [
  'MARKUP',
  'RRC_DISCOUNT',
]

const toText = (value: number | undefined | null) =>
  value === undefined || value === null ? '' : String(value)

const toUiColumn = (value: number | undefined) =>
  value === undefined ? '' : String(value + 1)

const toApiColumn = (value: string) => Number(value) - 1

const toOptionalNumber = (value: string) =>
  value.trim() === '' ? undefined : Number(value)

const toOptionalText = (value: string) => value.trim() || undefined

const buildSegmentRow = (
  segment: PriceSegmentSource,
  rule: SegmentRuleDto | undefined
): PriceSegmentRow => ({
  segmentId: segment.id,
  description: segment.description,
  defaultMarkupPercent: segment.markupPercentage,
  isRrc: segment.isRrc,
  enabled: rule ? true : segment.markupPercentage > 0,
  mode: rule?.mode ?? 'MARKUP',
  useSegmentDefault: rule?.useSegmentDefault ?? true,
  deltaPercent: toText(rule?.deltaPercent ?? 0),
  columnNumber: toUiColumn(rule?.column?.columnNumber),
  columnExpectedHeader: rule?.column?.expectedHeader ?? '',
})

export const buildPriceFormValues = (
  settings: ImportPriceSettingsDto | undefined,
  segments: PriceSegmentSource[]
): ConfigPriceFormValues => {
  const rulesById = new Map(
    (settings?.segments ?? []).map((rule) => [rule.segmentId, rule])
  )
  const hasSettings = settings !== undefined

  return {
    enabled: settings?.enabled ?? false,
    vatRatePercent: toText(
      settings?.vatRatePercent ?? DEFAULT_VAT_RATE_PERCENT
    ),
    purchaseColumnNumber: toUiColumn(settings?.purchase?.columnNumber),
    pricesIncludeVat: settings?.purchase?.includesVat ?? true,
    purchaseDiscountPercent: toText(settings?.purchase?.discountPercent ?? 0),
    purchaseExpectedHeader: settings?.purchase?.expectedHeader ?? '',
    currencyFromFile: settings?.currency?.columnNumber !== undefined,
    currencyFixedCode: String(
      settings?.currency?.fixedCode ?? DEFAULT_CURRENCY
    ),
    currencyColumnNumber: toUiColumn(settings?.currency?.columnNumber),
    currencyTextMapping: Object.entries(
      settings?.currency?.textMapping ?? {}
    ).map(([key, value]) => ({ key, value: String(value) })),
    rrcColumnNumber: toUiColumn(settings?.rrc?.columnNumber),
    rrcExpectedHeader: settings?.rrc?.expectedHeader ?? '',
    segments: segments.map((segment) => {
      const row = buildSegmentRow(segment, rulesById.get(segment.id))
      return hasSettings && !rulesById.has(segment.id)
        ? { ...row, enabled: false }
        : row
    }),
    minMarkupPercent: toText(settings?.guards?.minMarkupPercent),
    capByRrc: settings?.guards?.capByRrc ?? false,
    spikeAlertPercent: toText(settings?.guards?.spikeAlertPercent),
    maxInvalidPricePercent: toText(settings?.guards?.maxInvalidPricePercent),
  }
}

const buildColumn = (
  columnNumber: string,
  includesVat: boolean,
  expectedHeader: string
): PriceColumnDto => ({
  columnNumber: toApiColumn(columnNumber),
  includesVat,
  expectedHeader: toOptionalText(expectedHeader),
})

const buildCurrency = (values: ConfigPriceFormValues) => {
  if (!values.currencyFromFile) {
    return Number(values.currencyFixedCode) === DEFAULT_CURRENCY
      ? undefined
      : { fixedCode: Number(values.currencyFixedCode) }
  }
  const textMapping = Object.fromEntries(
    values.currencyTextMapping
      .filter((row) => row.key.trim() !== '' && row.value !== '')
      .map((row) => [row.key.trim(), Number(row.value)])
  )
  return {
    columnNumber: toApiColumn(values.currencyColumnNumber),
    textMapping: Object.keys(textMapping).length > 0 ? textMapping : undefined,
  }
}

const buildPurchase = (
  values: ConfigPriceFormValues
): PurchaseDto | undefined => {
  const common = {
    includesVat: values.pricesIncludeVat,
    discountPercent: toOptionalNumber(values.purchaseDiscountPercent) ?? 0,
  }
  if (values.purchaseColumnNumber.trim() !== '')
    return {
      ...common,
      source: 'COLUMN',
      columnNumber: toApiColumn(values.purchaseColumnNumber),
      expectedHeader: toOptionalText(values.purchaseExpectedHeader),
    }
  if (values.rrcColumnNumber.trim() !== '') return { ...common, source: 'RRC' }
  return undefined
}

const buildSegmentRule = (
  row: PriceSegmentRow,
  includesVat: boolean
): SegmentRuleDto => ({
  segmentId: row.segmentId,
  mode: row.mode,
  useSegmentDefault: row.mode === 'MARKUP' ? row.useSegmentDefault : false,
  deltaPercent: toOptionalNumber(row.deltaPercent) ?? 0,
  column:
    row.mode === 'COLUMN'
      ? buildColumn(row.columnNumber, includesVat, row.columnExpectedHeader)
      : undefined,
})

export const buildPriceSettings = (
  values: ConfigPriceFormValues
): ImportPriceSettingsDto => ({
  enabled: values.enabled,
  vatRatePercent: toOptionalNumber(values.vatRatePercent),
  purchase: buildPurchase(values),
  currency: buildCurrency(values),
  rrc:
    values.rrcColumnNumber.trim() === ''
      ? undefined
      : buildColumn(
          values.rrcColumnNumber,
          values.pricesIncludeVat,
          values.rrcExpectedHeader
        ),
  segments: values.segments
    .filter((row) => row.enabled)
    .map((row) => buildSegmentRule(row, values.pricesIncludeVat)),
  guards: {
    minMarkupPercent: toOptionalNumber(values.minMarkupPercent),
    capByRrc: values.capByRrc,
    spikeAlertPercent: toOptionalNumber(values.spikeAlertPercent),
    maxInvalidPricePercent: toOptionalNumber(values.maxInvalidPricePercent),
  },
})

export const getSegmentMarkupPercent = (
  row: Pick<
    PriceSegmentRow,
    'useSegmentDefault' | 'defaultMarkupPercent' | 'deltaPercent'
  >
) =>
  (row.useSegmentDefault ? row.defaultMarkupPercent : 0) +
  (toOptionalNumber(row.deltaPercent) ?? 0)

export const needsRrcColumn = (values: ConfigPriceFormValues) =>
  values.capByRrc ||
  values.segments.some(
    (row) =>
      row.enabled && (row.mode === 'RRC_DISCOUNT' || row.mode === 'RRC_COLUMN')
  )
